import { GraphQLFormattedError } from 'graphql';

/** GraphQL/HTTP extension codes that indicate an authorization failure. */
const AUTH_ERROR_CODES: ReadonlyArray<string> = [
  'UNAUTHORIZED',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'AUTH_NOT_AUTHORIZED',
  'AUTH_NOT_AUTHENTICATED'
];

/** Error thrown when an identity GraphQL operation returns a handled backend failure. */
export class RoleOperationError extends Error {
  public constructor(
    public readonly isAuthError: boolean,
    message: string
  ) {
    super(message);
    this.name = 'RoleOperationError';
  }
}

/** Returns true when an error extension `code` denotes an authorization failure. */
function isAuthErrorCode(code: unknown): boolean {
  return typeof code === 'string' && AUTH_ERROR_CODES.includes(code.toUpperCase());
}

/**
 * Determines whether an arbitrary error (Apollo network error, GraphQL error, or a
 * {@link RoleOperationError}) represents an authorization/unauthorized failure.
 * Used by callers to show a permission-specific message rather than a generic one.
 * @param error - The caught error.
 * @returns True when the error is an auth/permission failure.
 */
export function isUnauthorizedError(error: unknown): boolean {
  if (error instanceof RoleOperationError) {
    return error.isAuthError;
  }

  const candidate: {
    networkError?: { status?: number } | null;
    graphQLErrors?: ReadonlyArray<GraphQLFormattedError>;
  } = (error ?? {}) as never;

  const status: number | undefined = candidate.networkError?.status;
  if (status === 401 || status === 403) {
    return true;
  }

  return (candidate.graphQLErrors ?? []).some((gqlError: GraphQLFormattedError): boolean =>
    isAuthErrorCode(gqlError?.extensions?.['code'])
  );
}

/**
 * Throws a {@link RoleOperationError} when a GraphQL result carries errors.
 * Apollo runs with `errorPolicy: 'all'`, so backend authorization failures arrive in
 * `result.errors` rather than rejecting the observable; without this they would be
 * silently swallowed and the UI would report a false success.
 * @param errors - The GraphQL errors from the query or mutation result, if any.
 * @param fallbackMessage - Message used when the first GraphQL error has no message.
 */
export function throwIfGraphQLErrors(
  errors: ReadonlyArray<GraphQLFormattedError> | undefined,
  fallbackMessage: string
): void {
  if (!errors || errors.length === 0) {
    return;
  }
  const isAuth: boolean = errors.some((error: GraphQLFormattedError): boolean =>
    isAuthErrorCode(error.extensions?.['code'])
  );
  throw new RoleOperationError(isAuth, errors[0]?.message || fallbackMessage);
}
